import { useEffect, useId, useRef, useState } from 'react';
import type { StaffClaims } from '@macksons/web-client';
import type { OperatorScope } from '../auth/operator-scope';
import { useT } from '../hooks';
import { useServices } from '../services';
import { AccountIcon } from './icons';
import { LocaleSwitcher } from './LocaleSwitcher';
import { ScopeSelectors } from './ScopeSelectors';

/**
 * App-bar account menu: a disclosure button opening a popover with the language selector,
 * the company/outlet selector when more than one is assigned, the signed-in name and
 * Sign out. Closes on Escape, outside click, or pointer outside.
 */
export function AccountMenu({ claims, scope }: { claims: StaffClaims; scope: OperatorScope | undefined }) {
  const t = useT();
  const { session } = useServices();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hasSelectors = Boolean(scope && (scope.tenantIds.length > 1 || scope.outletIds.length > 1));

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="account" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="account__button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t('operator.account.open')}
        onClick={() => setOpen((v) => !v)}
      >
        <AccountIcon className="account__icon" />
      </button>
      {open && (
        <div id={menuId} className="account__menu" aria-label={t('operator.account.label')}>
          {hasSelectors && scope && <ScopeSelectors scope={scope} />}
          <LocaleSwitcher />
          <p className="account__user">{t('operator.header.signedInAs', { name: claims.name ?? claims.sub })}</p>
          <button type="button" className="button button--block" onClick={() => void session.signOut()}>
            {t('operator.header.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}
